import { ChakraFlex, DateFormat } from '@equip.health/ui';
import { TreatmentEpisodeStatus } from '~/lib/constants/patients.constants';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { PatientStatusHeaderPopoverBodyRow } from '~/components/maud/patients/profile/patient-header/PatientStatusHeaderPopoverBodyRow';
import { getActivePauseStartDate } from '~/lib/util/patientStatusHeader.util';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getDateTagProps } from '~/lib/util/date.util';

type Props = {
  inquiryDetail?: InquiryDetailResponse;
  insuranceAuthorizationEndDate?: string;
  lastContactDateISO?: string;
  pausedDetails?: EpisodePausedDetails[];
  primaryDiagnosis?: string;
  patientStatus: string;
  treatmentEpisode?: TreatmentEpisode;
  tentativeDischargeDate?: string;
};
export const PatientStatusHeaderPopoverBody = ({
  inquiryDetail,
  insuranceAuthorizationEndDate,
  lastContactDateISO,
  primaryDiagnosis,
  patientStatus,
  pausedDetails,
  treatmentEpisode,
  tentativeDischargeDate,
}: Props) => {
  const {
    displayInsuranceAuthEndingDates: isDisplayInsuranceAuthEndingDatesEnabled,
    enableDisplayInsurancePart1: isEnableDisplayInsurancePart1,
  } = useFlags<LDFlags>();

  const isInsuranceAuthEndingDatesActive =
    isDisplayInsuranceAuthEndingDatesEnabled && isEnableDisplayInsurancePart1;

  const shouldShowTentativeDischargeDate = isEnableDisplayInsurancePart1;
  const isInquiryStatus = useMemo(
    () =>
      Object.values(InquiryStatusValue).includes(
        patientStatus as InquiryStatusValue,
      ),
    [patientStatus],
  );
  const isTreatmentEpisodeStatus = Object.values(
    TreatmentEpisodeStatus,
  ).includes(patientStatus as TreatmentEpisodeStatus);

  const admitDateTime: DateTime | undefined = treatmentEpisode?.admitDate
    ? DateTime.fromFormat(
        treatmentEpisode.admitDate,
        DateFormat.shortYearMonthDay,
      )
    : undefined;

  const pausedDateTime = getActivePauseStartDate(pausedDetails);

  const dischargedDateTime: DateTime | undefined = treatmentEpisode?.closedDate
    ? DateTime.fromFormat(
        treatmentEpisode.closedDate,
        DateFormat.shortYearMonthDay,
      )
    : undefined;

  return (
    <ChakraFlex
      alignItems="flex-start"
      aria-label="Patient status header popover body"
      display="inline-flex"
      flexDirection="column"
      gap="12px"
      id="patient-status-header-popover-body"
      padding="18px 24px"
    >
      {isTreatmentEpisodeStatus && (
        <>
          <PatientStatusHeaderPopoverBodyRow
            label="Primary Dx"
            value={primaryDiagnosis}
          />
          {patientStatus === TreatmentEpisodeStatus.IN_TREATMENT && (
            <>
              <PatientStatusHeaderPopoverBodyRow
                label="Admit date"
                value={admitDateTime?.toFormat(DateFormat.longDate)}
              />
              <PatientStatusHeaderPopoverBodyRow
                label="Weeks at Equip"
                value={
                  admitDateTime
                    ? Math.ceil(
                        Math.abs(admitDateTime.diffNow(['weeks']).weeks),
                      ).toString(10)
                    : undefined
                }
              />
              {isInsuranceAuthEndingDatesActive && (
                <PatientStatusHeaderPopoverBodyRow
                  emptyStateText="None on file"
                  label="Insurance auth end"
                  tagProps={getDateTagProps(insuranceAuthorizationEndDate)}
                  value={
                    insuranceAuthorizationEndDate
                      ? DateTime.fromFormat(
                          insuranceAuthorizationEndDate,
                          DateFormat.shortYearMonthDay,
                        ).toFormat(DateFormat.longDate)
                      : undefined
                  }
                />
              )}
              {shouldShowTentativeDischargeDate && (
                <PatientStatusHeaderPopoverBodyRow
                  label="Tentative discharge"
                  tagProps={getDateTagProps(tentativeDischargeDate)}
                  value={
                    tentativeDischargeDate
                      ? DateTime.fromFormat(
                          tentativeDischargeDate,
                          DateFormat.shortYearMonthDay,
                        ).toFormat(DateFormat.longDate)
                      : undefined
                  }
                />
              )}
            </>
          )}
          {patientStatus === TreatmentEpisodeStatus.PAUSED && (
            <PatientStatusHeaderPopoverBodyRow
              label="Pause date"
              value={pausedDateTime}
            />
          )}
          {patientStatus === TreatmentEpisodeStatus.DISCHARGED && (
            <>
              <PatientStatusHeaderPopoverBodyRow
                label="Discharge reason"
                value={treatmentEpisode?.statusReason}
              />
              <PatientStatusHeaderPopoverBodyRow
                label="Discharge date"
                value={dischargedDateTime?.toFormat(DateFormat.mmDdYySlashes)}
              />
            </>
          )}
        </>
      )}
      {isInquiryStatus && (
        <>
          <PatientStatusHeaderPopoverBodyRow
            label="Channel"
            value={inquiryDetail?.channel?.name}
          />
          <PatientStatusHeaderPopoverBodyRow
            label="Channel type"
            value={inquiryDetail?.channelType?.name}
          />
          {patientStatus === InquiryStatusValue.NEW && (
            <PatientStatusHeaderPopoverBodyRow
              label="Created date"
              value={
                inquiryDetail?.createdAt
                  ? DateTime.fromISO(inquiryDetail.createdAt).toFormat(
                      DateFormat.mmDdYySlashes,
                    )
                  : undefined
              }
            />
          )}
          {[
            InquiryStatusValue.FIELDED,
            InquiryStatusValue.QUALIFIED,
            InquiryStatusValue.ASSESSING,
            InquiryStatusValue.ONBOARDING,
          ].includes(patientStatus as InquiryStatusValue) && (
            <PatientStatusHeaderPopoverBodyRow
              label="Last contact date"
              value={
                lastContactDateISO
                  ? DateTime.fromISO(lastContactDateISO).toFormat(
                      DateFormat.mmDdYySlashes,
                    )
                  : undefined
              }
            />
          )}
          {patientStatus === InquiryStatusValue.ENROLLED && (
            <PatientStatusHeaderPopoverBodyRow
              label="Admit date"
              value={
                inquiryDetail?.targetAdmissionDate
                  ? DateTime.fromISO(
                      inquiryDetail?.targetAdmissionDate,
                    ).toFormat(DateFormat.mmDdYySlashes)
                  : undefined
              }
            />
          )}
          {patientStatus === InquiryStatusValue.CLOSED && (
            <>
              <PatientStatusHeaderPopoverBodyRow
                label="Closed reason"
                value={inquiryDetail?.closedReason?.description}
              />
              <PatientStatusHeaderPopoverBodyRow
                label="Closed date"
                value={
                  inquiryDetail?.closedAt || inquiryDetail?.updatedAt
                    ? DateTime.fromISO(
                        inquiryDetail?.closedAt || inquiryDetail?.updatedAt,
                      ).toFormat(DateFormat.mmDdYySlashes)
                    : undefined
                }
              />
            </>
          )}
        </>
      )}
    </ChakraFlex>
  );
};
